import React, { useRef, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AuditDiffTable from "./AuditDiffTable";
import AuditOrderItemsDiff from "./AuditOrderItemsDiff";
import { formatRODate } from "../utils/formatDate";
import { useTheme } from "../context/ThemeContext";

const AuditLogModal = ({ open, onClose, log }) => {

    const { darkMode } = useTheme();
    const itemsRef = useRef(null);

    const scrollToItems = () => {
        setTimeout(() => {
            if (itemsRef.current) {
                itemsRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }
        }, 50);
    };

    if (!log) return null;

    const parse = (json) => {
        try {
            return json ? JSON.parse(json) : null;
        } catch {
            return null;
        }
    };

    const oldObj = parse(log.oldValues);
    const newObj = parse(log.newValues);

    const isOrder = log.entityName === "Order";
    const isOffer = log.entityName === "Offer";

    const safeOld = oldObj ?? {};
    const safeNew = newObj ?? {};

    const oldHasItems = oldObj && Array.isArray(oldObj.Items);
    const newHasItems = newObj && Array.isArray(newObj.Items);

    let baseOld = { ...safeOld };
    let baseNew = { ...safeNew };

    if (isOrder) {
        delete baseOld.Items;
        delete baseNew.Items;
    }

    if (isOffer) {
        delete baseOld.Items;
        delete baseNew.Items;
    }

    const actionTypeMap = {
        Create: "Creat",
        Update: "Modificat",
        Delete: "Șters",

        StatusUpdate: "Actualizare status",
        PasswordChanged: "Parolă schimbată",

        AdvanceCreated: "Creat",
        AdvanceDeleted: "Șters",

        UserCreated: "Creat",
        UserUpdated: "Modificat",
        UserDeleted: "Șters",

        ProductCreated: "Creat",
        ProductUpdated: "Modificat",
        ProductDeleted: "Șters",

        OfferCreated: "Creat",
        OfferUpdated: "Modificat",
        OfferDeleted: "Șters",
    };

    const entityNameMap = {
        Customer: "Client",
        User: "Utilizator",
        Order: "Comandă",
        Product: "Produs",
        Offer: "Ofertă",
        AdvancePayment: "Avans",
        CommissionApproval: "Aprobare comision",
    };

    const businessTypeMap = {
        0: "Persoană fizică",
        1: "Firmă",
        2: "PFA",
    };

    const customerStatusMap = {
        0: "Inactiv",
        1: "Activ",
        2: "Refuzat",
    };

    const offerClientTypeMap = {
        "PersoanaFizica": "Persoană fizică",
        "PersoanaJuridica": "Persoană juridică",
    };

    ["Type", "TipAfacere", "BusinessType"].forEach(k => {
        if (baseOld[k] !== undefined) baseOld[k] = businessTypeMap[baseOld[k]] ?? baseOld[k];
        if (baseNew[k] !== undefined) baseNew[k] = businessTypeMap[baseNew[k]] ?? baseNew[k];
    });

    ["Status", "ClientStatus", "StatusClient"].forEach(k => {
        if (baseOld[k] !== undefined) baseOld[k] = customerStatusMap[baseOld[k]] ?? baseOld[k];
        if (baseNew[k] !== undefined) baseNew[k] = customerStatusMap[baseNew[k]] ?? baseNew[k];
    });

    ["TipClient"].forEach(k => {
        if (baseOld[k] !== undefined) baseOld[k] = offerClientTypeMap[baseOld[k]] ?? baseOld[k];
        if (baseNew[k] !== undefined) baseNew[k] = offerClientTypeMap[baseNew[k]] ?? baseNew[k];
    });

    const isCreate = log.actionType.toLowerCase().includes("create");

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: {
                    borderRadius: "14px",
                    overflow: "hidden",
                    backgroundColor: darkMode ? "#1f2937" : "#ffffff",
                    color: darkMode ? "#f3f4f6" : "#000000",
                },
            }}
        >
            {/* CÍM */}
            <DialogTitle
                className={`flex justify-between items-center text-xl font-semibold
        ${darkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-black"}`}
            >
                Detalii Audit
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent className={`space-y-6 ${darkMode ? "bg-gray-900 text-gray-200" : "bg-white text-black"}`}
            >

                {/* HEADER BLOKK */}
                <div className="space-y-1 text-[15px] leading-relaxed">
                    <div><b>Utilizator:</b> {log.userName}</div>
                    <div><b>Rol:</b> {log.role}</div>
                    <div><b>Data:</b> {formatRODate(log.createdAt)}</div>

                    <div className="flex items-center gap-2">
                        <p><b>Acțiune:</b> {actionTypeMap[log.actionType] || log.actionType}</p>

                    </div>

                    <div><b>Entitate:</b> {entityNameMap[log.entityName] || log.entityName}</div>

                </div>

                {/* DIFF --- mezők */}
                <AuditDiffTable
                    oldObj={baseOld}
                    newObj={baseNew}
                    isCreate={isCreate}
                />

                {/* ORDER ITEMS --- ha létezik */}
                {isOrder && (oldHasItems || newHasItems) && (
                    <div ref={itemsRef}>
                        <h3 className="font-semibold text-lg mb-3">Modificări produse</h3>

                        <AuditOrderItemsDiff
                            oldItems={oldObj?.Items || []}
                            newItems={newObj?.Items || []}
                            isCreate={isCreate}
                            onItemOpen={scrollToItems}
                        />
                    </div>
                )}

                {/* OFFER ITEMS */}
                {isOffer && (oldHasItems || newHasItems) && (
                    <div ref={itemsRef}>
                        <h3 className="font-semibold text-lg mb-3">Modificări produse (Ofertă)</h3>

                        <AuditOrderItemsDiff
                            oldItems={oldObj?.Items || []}
                            newItems={newObj?.Items || []}
                            isCreate={isCreate}
                            isOffer={isOffer}
                            onItemOpen={scrollToItems}
                        />
                    </div>
                )}

            </DialogContent>
        </Dialog>
    );
};

export default AuditLogModal;
